import React, { useState, useEffect } from "react";
import SlidingText from "../components/SlidingText";
import CharFadeIn from "../components/CharFadeIn";
import { motion, AnimatePresence } from "framer-motion";

const Checkmark = () => (
  <motion.svg
    width="80"
    height="80"
    viewBox="0 0 100 100"
    initial="hidden"
    animate="visible"
    className="mb-4"
  >
    <motion.circle
      cx="50"
      cy="50"
      r="48"
      stroke="#000000"
      strokeWidth="4"
      fill="none"
      variants={{
        hidden: { strokeDasharray: "0, 290", opacity: 0 },
        visible: {
          strokeDasharray: "290, 290",
          opacity: 1,
          transition: { duration: 0.8, ease: "easeInOut" },
        },
      }}
    />
    <motion.path
      d="M30,50 L45,65 L70,35"
      stroke="#000000"
      strokeWidth="6"
      fill="none"
      strokeLinecap="round"
      strokeLinejoin="round"
      variants={{
        hidden: { pathLength: 0, opacity: 0 },
        visible: {
          pathLength: 1,
          opacity: 1,
          transition: { delay: 0.5, duration: 0.5, ease: "easeOut" },
        },
      }}
    />
  </motion.svg>
);

function Contact() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: "",
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const validate = () => {
    let tempErrors = {};
    if (!formData.name) tempErrors.name = "Name is required.";
    if (!formData.email) {
      tempErrors.email = "Email is required.";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      tempErrors.email = "Email is not valid.";
    }
    if (!formData.message) tempErrors.message = "Message is required.";
    setErrors(tempErrors);
    return Object.keys(tempErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validate()) {
      setIsLoading(true);
      setTimeout(() => {
        setIsLoading(false);
        setIsSubmitted(true);
        setFormData({ name: "", email: "", message: "" });
        setTimeout(() => setIsSubmitted(false), 5000); // Hide success message after 5s
      }, 2000);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="bg-black text-white px-6 md:px-20 py-32 md:py-48 text-center relative overflow-hidden">
        <div className="absolute inset-0 flex items-center justify-center z-10 pointer-events-none select-none opacity-20">
          <SlidingText
            text="Contact"
            className="font-bold text-center text-[120px] md:text-[280px] text-white"
          />
        </div>
        <div className="relative z-20">
          <CharFadeIn
            text="Let's Talk"
            className="text-6xl md:text-8xl font-bold text-white leading-tight"
          />
          <p className="mt-4 text-lg md:text-xl text-gray-300 max-w-2xl mx-auto">
            Have a question or want to work together? Drop us a line.
          </p>
        </div>
      </section>

      {/* Contact Form and Details Section */}
      <section className="px-6 md:px-20 py-16 md:py-24">
        <div className="max-w-4xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-16">
            {/* Contact Form */}
            <div className="md:col-span-2 relative">
              <AnimatePresence>
                {isLoading && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="absolute inset-0 bg-white/80 flex items-center justify-center z-30"
                  >
                    <div className="w-16 h-16 border-4 border-black border-t-transparent rounded-full animate-spin"></div>
                  </motion.div>
                )}
                {isSubmitted && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    className="absolute inset-0 bg-white flex items-center justify-center z-30"
                  >
                    <div className="text-center flex flex-col items-center">
                      <Checkmark />
                      <h3 className="text-2xl font-bold text-black mb-2">
                        Thank you!
                      </h3>
                      <p className="text-gray-600">
                        Your message has been sent successfully.
                      </p>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
              <CharFadeIn
                text="Send us a Message"
                className="text-3xl font-bold text-black mb-8 block"
              />
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="name" className="sr-only">
                    Name
                  </label>
                  <input
                    type="text"
                    name="name"
                    id="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Your Name"
                    className={`w-full px-4 py-3 bg-gray-100 rounded-lg focus:outline-none border-2 transition-colors ${
                      errors.name
                        ? "border-red-500"
                        : "border-transparent focus:border-black"
                    }`}
                  />
                  {errors.name && (
                    <p className="text-red-500 text-sm mt-1">{errors.name}</p>
                  )}
                </div>
                <div>
                  <label htmlFor="email" className="sr-only">
                    Email
                  </label>
                  <input
                    type="email"
                    name="email"
                    id="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="Your Email"
                    className={`w-full px-4 py-3 bg-gray-100 rounded-lg focus:outline-none border-2 transition-colors ${
                      errors.email
                        ? "border-red-500"
                        : "border-transparent focus:border-black"
                    }`}
                  />
                  {errors.email && (
                    <p className="text-red-500 text-sm mt-1">{errors.email}</p>
                  )}
                </div>
                <div>
                  <label htmlFor="message" className="sr-only">
                    Message
                  </label>
                  <textarea
                    name="message"
                    id="message"
                    rows="5"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Your Message"
                    className={`w-full px-4 py-3 bg-gray-100 rounded-lg focus:outline-none border-2 transition-colors ${
                      errors.message
                        ? "border-red-500"
                        : "border-transparent focus:border-black"
                    }`}
                  ></textarea>
                  {errors.message && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.message}
                    </p>
                  )}
                </div>
                <div>
                  <button
                    type="submit"
                    className="w-full px-6 py-4 bg-black text-white font-bold rounded-lg hover:bg-gray-800 transition-colors duration-300"
                  >
                    Send Message
                  </button>
                </div>
              </form>
            </div>

            {/* Contact Details */}
            <div className="text-left">
              <CharFadeIn
                text="Contact Information"
                className="text-3xl font-bold text-black mb-8 block"
              />
              <div className="space-y-6 text-lg text-gray-700">
                <div>
                  <p className="font-semibold text-black">Address:</p>
                  <p>Lilly Corporate Center, Indianapolis, IN 46285</p>
                </div>
                <div>
                  <p className="font-semibold text-black">Phone:</p>
                  <p>1-800-545-5979</p>
                </div>
                <div>
                  <p className="font-semibold text-black">Email:</p>
                  <p>support@mounjaro.com</p>
                </div>
                <div>
                  <p className="font-semibold text-black">Hours:</p>
                  <p>Mon - Fri: 9:00 AM - 5:00 PM EST</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Contact;
